import React, { useEffect } from 'react';
import { ReactNode } from 'react';
import { inject, observer } from 'mobx-react';
import { IRichTextItemInternal, IRichTextItemsInternal, NodeType, IAttributeInfo } from '../stores/types';
import classNames from 'classnames';
import { MainStore } from '../stores/MainStore';
import styles from './richText.scss';
import { decodeHTML } from './html-helper';

interface IRichTextProps {
    store?: MainStore;
    richTextId: string;
    injectedRichTexts?: IRichTextItemsInternal[];
}
export const RichText = inject('store')(
    observer((props: IRichTextProps) => {
        const refRTF = React.useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (refRTF.current) {
                const links = refRTF.current.querySelectorAll('.pdp-richtext_line_a');

                if (links && links.length > 0) {
                    links.forEach(link => {
                        let currentUrl = link.getAttribute('href');

                        if(!currentUrl) return;
                        
                        if (currentUrl.includes('itemorigin')) {
                            const urlParamsArray = currentUrl.split('?');
                            const placeOfTag = urlParamsArray.findIndex(it => it.includes('itemorigin='));
                            urlParamsArray[placeOfTag] = store?.productInfoItemOrigin.substring(1) || '';
                            currentUrl = urlParamsArray.join('?');
                        } else {
                            currentUrl = currentUrl.concat(store?.productInfoItemOrigin || '');
                        }

                        link.setAttribute('href', currentUrl);
                    });
                }
            }

        }, []);

        const {
            store,
            richTextId,
            injectedRichTexts,
        } = props;
        const injectedRichTextExist = injectedRichTexts && injectedRichTexts.length;
        const allRichTexts = injectedRichTextExist ? props.injectedRichTexts : store?.fetchedData.allRichTexts;
        
        if(!allRichTexts) return <></>;
        
        const richTextItemsSelected: IRichTextItemInternal[] | undefined 
            = allRichTexts.find(rit=>rit.id === richTextId)?.items;

        if(!richTextItemsSelected || !richTextItemsSelected.length) return <></>;

        function MapLines(richTextItems: IRichTextItemInternal[]): ReactNode {
            return richTextItems.map((x, index)=> MapLine(x, index));
        }

        function MapLine(richTextItem: IRichTextItemInternal, index: number): ReactNode {
            if (richTextItem.nodeType == NodeType.LineBreak) return <br key={index} data-testid='br_type_elm' />;

            if (richTextItem.nodeType == NodeType.BlankLine)
                return <div key={index} className={classNames(getClasses(richTextItem.markups, true, false))}
                    data-testid='bl_type_elm'></div>;

            if (richTextItem.nodeType == NodeType.Paragraph) {
                return (
                    <div key={index} className={classNames(getClasses(richTextItem?.markups, false, false))}
                        data-testid='paragraph_type_elm'>
                        {MapLines(richTextItem.children)}
                    </div>);
            }
            if (richTextItem.nodeType == NodeType.List) {
                return (
                    <ul key={index} className={'pdp-richtext_list'} data-testid='ul_type_elm'>
                        {MapLines(richTextItem.children)}
                    </ul>
                )
            }
            if (richTextItem.nodeType == NodeType.Text) {
                if (richTextItem.link && richTextItem.link.href) {
                    if (richTextItem.link.openInNewTab)
                        return (
                            <a key={index}
                                className={classNames(getClasses(richTextItem?.markups, true, true))}
                                data-testid='a_type_elm' href={richTextItem.link.href} 
                                    target='_blank' rel='noreferrer'
                                ref={(el) => addAttributes(el, richTextItem.attributes)}>
                                {decodeHTML(richTextItem.textValue)}
                            </a>);
                    else
                        return (
                            <a key={index}
                                className={classNames(getClasses(richTextItem?.markups, true, true))}
                                data-testid='a_type_elm' href={richTextItem.link.href}
                                ref={(el) => addAttributes(el, richTextItem.attributes)}>
                                {decodeHTML(richTextItem.textValue)}
                            </a>);
                }
                else
                    return (
                        <span key={index} className={classNames(getClasses(richTextItem.markups, true, false))}
                            data-testid='span_type_elm' ref={(el) => addAttributes(el, richTextItem.attributes)}>
                            {decodeHTML(richTextItem.textValue)}
                        </span>);
            }
            if (richTextItem.nodeType == NodeType.ListItem) {
                const children = richTextItem.children && richTextItem.children.length
                    ? MapLines(richTextItem.children)
                    : null;

                if (richTextItem.link && richTextItem.link.href) {
                    if (richTextItem.link.openInNewTab)
                        return (
                            <li key={index} className={classNames(getClasses(richTextItem.markups, true, false))}
                                data-testid='li_type_elm'>
                                <a key={index} className={styles.richtext_line_a}
                                    data-testid='a_type_elm' href={richTextItem.link.href} 
                                    target='_blank' rel='noreferrer'>
                                    {decodeHTML(richTextItem.textValue)}
                                    {children}
                                </a>
                            </li>);
                    else
                        return (
                            <li key={index} className={classNames(getClasses(richTextItem.markups, true, false))}
                                data-testid='li_type_elm'>
                                <a key={index} className={styles.richtext_line_a}
                                    data-testid='a_type_elm' href={richTextItem.link.href}>
                                    {decodeHTML(richTextItem.textValue)}
                                    {children}
                                </a>
                            </li>);
                }
                else
                    return (
                        <li key={index} className={classNames(getClasses(richTextItem.markups, true, false))}
                            data-testid='li_type_elm'>
                            {decodeHTML(richTextItem.textValue)}
                            {children}
                        </li>);
            }
        }

        function getClasses(
            markups: string[],
            extendWithBulletStyle: boolean,
            extendWithAnchorStyle: boolean): string[] {

            let classes: string[] = [];
            if (extendWithBulletStyle)
                classes = classes.concat('pdp-richtext_bullet');

            if (markups && markups.length) {
                classes = classes.concat(markups.map(x=>'pdp-richtext_'+x))
            }

            if (extendWithAnchorStyle)
                classes = classes.concat(styles.richtext_line_a);

            return classes;
        }

        function addAttributes(
            el: HTMLElement | null, attributes: IAttributeInfo[] | null): void {

            if (el && attributes && attributes.length) {
                attributes.forEach(function (attribute) {
                    if (attribute.name) {
                        el.setAttribute(attribute.name, attribute.value);
                    }
                });
            }
        }
        
        return <div className = {styles.richtext_root} data-testid='richtext_root' ref={refRTF}>
            {MapLines(richTextItemsSelected)}
        </div>
}));
